import type { LegViewModel } from 'flights/legViewModel';
import type { TripViewModel } from 'flights/tripViewModel';
import { partition, uniqBy } from 'lodash-es';
import type { SortOrder } from 'sortOrder';

import { separateFirstItem } from '@helpers/utils';

import type { FlightSortType } from '../types/flights/flightSortType';

/**
 * Sorts flight fares based on price
 */
export function sortFlightFares<T extends { price: FlightSearchResultFarePrice }>(
  fares: Array<T>,
  sortOrder: SortOrder,
  isIncludingTaxOrFee: boolean,
): Array<T> {
  return [...fares].sort((aFare: T, bFare: T) => {
    const aFlightFareAmountUsd = aFare.price.amountUsd;
    const aFlightFareOriginalAmountUsd = aFare.price.originalAmountUsd;
    const bFlightFareAmountUsd = bFare.price.amountUsd;
    const bFlightFareOriginalAmountUsd = bFare.price.originalAmountUsd;

    if (isIncludingTaxOrFee) {
      if (sortOrder === 'asc') {
        return aFlightFareAmountUsd - bFlightFareAmountUsd;
      }
      return bFlightFareAmountUsd - aFlightFareAmountUsd;
    } else {
      if (sortOrder === 'asc') {
        return aFlightFareOriginalAmountUsd - bFlightFareOriginalAmountUsd;
      }
      return bFlightFareOriginalAmountUsd - aFlightFareOriginalAmountUsd;
    }
  });
}

export function sortTripViewModels(
  tripViewModels: Array<TripViewModel>,
  sortType: FlightSortType,
  sortOrder: SortOrder,
) {
  switch (sortType) {
    case 'price':
      return sortTripViewModelBasedOnPrice(tripViewModels, sortOrder, true);
    case 'duration':
      return sortTripViewModelBasedOnTotalDuration(tripViewModels, sortOrder);
    case 'score':
      return sortTripViewModelBasedOnScoreAndHighlightItineraries(tripViewModels);
    case 'leg1_departure_time':
      return sortTripViewModelBasedOnLegOutboundTime(tripViewModels, sortOrder, 1);
    case 'leg2_departure_time':
      return sortTripViewModelBasedOnLegOutboundTime(tripViewModels, sortOrder, 2);
    case 'leg3_departure_time':
      return sortTripViewModelBasedOnLegOutboundTime(tripViewModels, sortOrder, 3);
    case 'leg4_departure_time':
      return sortTripViewModelBasedOnLegOutboundTime(tripViewModels, sortOrder, 4);
    case 'leg5_departure_time':
      return sortTripViewModelBasedOnLegOutboundTime(tripViewModels, sortOrder, 5);
    case 'leg6_departure_time':
      return sortTripViewModelBasedOnLegOutboundTime(tripViewModels, sortOrder, 6);
    default:
      return tripViewModels;
  }
}

/**
 * Sorts trip view models based on price
 */
export function sortTripViewModelBasedOnPrice(
  tripViewModels: Array<TripViewModel>,
  sortOrder: SortOrder,
  isIncludingTaxOrFee: boolean,
): Array<TripViewModel> {
  return [...tripViewModels].sort(
    (aTripViewModel: TripViewModel, bTripViewModel: TripViewModel) => {
      const aFlightFareViewModel: FareViewModel = aTripViewModel.filteredFares[0];
      const bFlightFareViewModel: FareViewModel = bTripViewModel.filteredFares[0];

      const aFlightFareAmountUsd = aFlightFareViewModel?.fare.price.amountUsd;
      const aFlightFareOriginalAmountUsd = aFlightFareViewModel?.fare.price.originalAmountUsd;
      const bFlightFareAmountUsd = bFlightFareViewModel?.fare.price.amountUsd;
      const bFlightFareOriginalAmountUsd = bFlightFareViewModel?.fare.price.originalAmountUsd;

      if (isIncludingTaxOrFee) {
        if (sortOrder === 'asc') {
          return aFlightFareAmountUsd - bFlightFareAmountUsd;
        }
        return bFlightFareAmountUsd - aFlightFareAmountUsd;
      } else {
        if (sortOrder === 'asc') {
          return aFlightFareOriginalAmountUsd - bFlightFareOriginalAmountUsd;
        }
        return bFlightFareOriginalAmountUsd - aFlightFareOriginalAmountUsd;
      }
    },
  );
}

/**
 * Sorts trip view models based on total duration
 */
export function sortTripViewModelBasedOnTotalDuration(
  tripViewModels: Array<TripViewModel>,
  sortOrder: SortOrder,
): Array<TripViewModel> {
  return [...tripViewModels].sort(
    (aTripViewModel: TripViewModel, bTripViewModel: TripViewModel) => {
      const aTripTotalDurationMinutes = aTripViewModel.legs.reduce(
        (accumulator: number, legViewModel: LegViewModel) => {
          return accumulator + legViewModel.leg.durationMinutes;
        },
        0,
      );

      const bTripTotalDurationMinutes = bTripViewModel.legs.reduce(
        (accumulator: number, legViewModel: LegViewModel) => {
          return accumulator + legViewModel.leg.durationMinutes;
        },
        0,
      );

      if (sortOrder === 'asc') {
        return aTripTotalDurationMinutes - bTripTotalDurationMinutes;
      }
      return bTripTotalDurationMinutes - aTripTotalDurationMinutes;
    },
  );
}

/**
 * Sorts trip view models based on score
 */
export const sortTripViewModelsBasedOnScoreLogic = (
  aTripViewModel: TripViewModel,
  bTripViewModel: TripViewModel,
) => {
  const aTripTotalScore = aTripViewModel.filteredFares[0]?.fare?.score || 0;
  const bTripTotalScore = bTripViewModel.filteredFares[0]?.fare?.score || 0;

  return bTripTotalScore - aTripTotalScore;
};

/**
 * Sorts trip view models based on departure time
 */
export const sortTripViewModelsBasedOnDepartureTimeLogic = (
  aTripViewModel: TripViewModel,
  bTripViewModel: TripViewModel,
  legIndex?: number,
) => {
  const aDeparture = aTripViewModel.legs[legIndex || 0]?.leg.departureTimeMinutes || 0;
  const bDeparture = bTripViewModel.legs[legIndex || 0]?.leg.departureTimeMinutes || 0;
  return aDeparture - bDeparture;
};

/**
 * Sorts trip view models based on score and departure time
 */
export const sortTripViewModelsBasedOnScoreAndDepartureTime = (
  tripViewModels: TripViewModel[],
): TripViewModel[] => {
  return [...tripViewModels].sort((aTripViewModel, bTripViewModel) => {
    const aTripTotalScore = aTripViewModel.filteredFares[0]?.fare?.score || 0;
    const bTripTotalScore = bTripViewModel.filteredFares[0]?.fare?.score || 0;

    // Compare by experience score first (descending)
    if (bTripTotalScore !== aTripTotalScore) {
      return bTripTotalScore - aTripTotalScore;
    }

    // If experience scores are the same, compare by departure time (ascending)
    return sortTripViewModelsBasedOnDepartureTimeLogic(aTripViewModel, bTripViewModel);
  });
};

/**
 * Sorts trip view models based on score and highlights itineraries
 */
export function sortTripViewModelBasedOnScoreAndHighlightItineraries(
  tripViewModels: Array<TripViewModel>,
  numberOfDecimalsInCurrency: number = 0,
): Array<TripViewModel> {
  const sortedTripViewModels = tripViewModels.slice().sort(sortTripViewModelsBasedOnScoreLogic);

  if (sortedTripViewModels[0]) {
    const bestTripViewModel: TripViewModel = { ...sortedTripViewModels[0], tripCardTags: ['best'] };

    sortedTripViewModels.splice(0, 1, bestTripViewModel);
  }

  const cheapestHighlightedTripViewModels = highlightCheapestTripViewModels(
    sortedTripViewModels,
    numberOfDecimalsInCurrency,
  );

  return highlightCheapestDirectTripViewModels(
    cheapestHighlightedTripViewModels,
    numberOfDecimalsInCurrency,
  );
}

/**
 * Sorts trip view models based on leg outbound time
 */
export function sortTripViewModelBasedOnLegOutboundTime(
  tripViewModels: Array<TripViewModel>,
  sortOrder: SortOrder,
  leg: number,
): Array<TripViewModel> {
  return [...tripViewModels].sort(
    (aTripViewModel: TripViewModel, bTripViewModel: TripViewModel) => {
      const legNumber = leg <= aTripViewModel.legs.length ? leg - 1 : 0;

      const aTripLegViewModel = aTripViewModel.legs[legNumber];
      const bTripLegViewModel = bTripViewModel.legs[legNumber];

      if (sortOrder === 'asc') {
        return (
          aTripLegViewModel.leg.departureTimeMinutes - bTripLegViewModel.leg.departureTimeMinutes
        );
      }
      return (
        bTripLegViewModel.leg.departureTimeMinutes - aTripLegViewModel.leg.departureTimeMinutes
      );
    },
  );
}

/**
 * Groups trip view models for new flight search recommended sort
 */
export const groupTripViewModelForNewFlightSearch2RecommendedSort = (
  tripViewModels: TripViewModel[],
  legIndex?: number,
): TripViewModel[] => {
  // Split recommended trips
  const [recommendedTrips, otherTrips] = partition(tripViewModels, (trip) => trip.tripCardTags);

  // Split the best cheapest trip
  const allCheapestTagTrips = recommendedTrips.filter((trip) =>
    trip.tripCardTags?.includes('cheapest'),
  );
  const sortedAllCheapestTagTrips =
    sortTripViewModelsBasedOnScoreAndDepartureTime(allCheapestTagTrips);
  const [firstCheapestTagTrip, otherCheapestTagTrips] =
    separateFirstItem(sortedAllCheapestTagTrips);

  // Split the best cheapest direct trip
  const allCheapestDirectTagTrips = recommendedTrips.filter((trip) =>
    trip.tripCardTags?.includes('cheapest_direct'),
  );
  const sortedAllCheapestDirectTagTrips =
    sortTripViewModelsBasedOnScoreAndDepartureTime(allCheapestDirectTagTrips);
  const [firstCheapestDirectTagTrip, otherCheapestDirectTagTrips] = separateFirstItem(
    sortedAllCheapestDirectTagTrips,
  );

  // Group the best tagged trip and the best direct trip and the best cheapest trip into final recommended section
  const bestTagTrip = recommendedTrips.filter((trip) => trip.tripCardTags?.includes('best'));

  const conciseRecommendedTrips = uniqBy(
    bestTagTrip.concat(firstCheapestDirectTagTrip, firstCheapestTagTrip),
    'tripId',
  ).map((trip) => {
    trip.recommendedSortGroup = 'recommended';
    return trip;
  });

  const otherTripsWithOtherCheapestAndCheapestDirectTrips = otherCheapestTagTrips.concat(
    otherCheapestDirectTagTrips,
    otherTrips,
  );

  // Spit direct trips and normal trips
  const [rawDirectTrips, rawNonDirectTrips] = partition(
    otherTripsWithOtherCheapestAndCheapestDirectTrips,
    (trip) => {
      if (legIndex !== undefined) {
        return trip.legs[legIndex].leg.stopoversCount === 0;
      }

      return trip.legs.every((leg) => leg.leg.stopoversCount === 0);
    },
  );

  // Sort by 'departureTime' (ascending order) for direct trips
  rawDirectTrips.sort((a, b) => sortTripViewModelsBasedOnDepartureTimeLogic(a, b, legIndex));

  const directTrips = uniqBy(
    rawDirectTrips.map((trip) => {
      trip.recommendedSortGroup = 'other_direct';
      return trip;
    }),
    'tripId',
  );

  // For all others, sort by 'score' (ascending order)
  rawNonDirectTrips.sort(sortTripViewModelsBasedOnScoreLogic);

  const nonDirectTrips = uniqBy(
    rawNonDirectTrips.map((trip) => {
      trip.recommendedSortGroup = 'other';
      return trip;
    }),
    'tripId',
  );

  return [
    ...conciseRecommendedTrips,
    // Add header for direct trips
    ...((directTrips.length > 0
      ? [
          {
            fares: [],
            filteredFares: [],
            legs: [],
            tripId: '',
            headerType: 'direct',
          },
        ]
      : []) as TripViewModel[]),
    ...directTrips,
    // Add header for other trips
    ...((nonDirectTrips.length > 0
      ? [
          {
            fares: [],
            filteredFares: [],
            legs: [],
            tripId: '',
            headerType: 'other',
          },
        ]
      : []) as TripViewModel[]),
    ...nonDirectTrips,
  ];
};

/**
 * Highlights cheapest trip view models
 */
function highlightCheapestTripViewModels(
  tripViewModels: Array<TripViewModel>,
  numberOfDecimalsInCurrency: number,
): Array<TripViewModel> {
  if (tripViewModels.length) {
    const { cheapestTripViewModel, cheapestTripViewModelIndex } = getCheapestTripViewModel(
      tripViewModels,
      numberOfDecimalsInCurrency,
    );

    if (cheapestTripViewModelIndex !== 0) {
      tripViewModels.splice(cheapestTripViewModelIndex, 1);
      tripViewModels.splice(1, 0, cheapestTripViewModel);
    }

    const cheapestPrice =
      cheapestTripViewModel.filteredFares[0]?.fare?.price.amountWithFraction || 0;

    const cheapestPriceWithDecimals = +cheapestPrice.toFixed(numberOfDecimalsInCurrency);

    return tripViewModels.map((tripViewModel) => {
      const tripViewModelPrice =
        tripViewModel.filteredFares[0]?.fare?.price.amountWithFraction || 0;

      const tripViewModelPriceWithDecimals = +tripViewModelPrice.toFixed(
        numberOfDecimalsInCurrency,
      );

      if (tripViewModelPriceWithDecimals === cheapestPriceWithDecimals) {
        return {
          ...tripViewModel,
          tripCardTags: [...(tripViewModel?.tripCardTags || []), 'cheapest'],
        };
      }

      return tripViewModel;
    });
  }

  return tripViewModels;
}

/**
 * Gets the cheapest trip view model
 */
function getCheapestTripViewModel(
  tripViewModels: Array<TripViewModel>,
  numberOfDecimalsInCurrency: number,
): {
  cheapestTripViewModel: TripViewModel;
  cheapestTripViewModelIndex: number;
} {
  let cheapestTripViewModel: TripViewModel = tripViewModels[0];
  let cheapestTripViewModelIndex: number = 0;

  for (let index = 1; index < tripViewModels.length; index++) {
    const cheapestTripViewModelMinFareViewModel = cheapestTripViewModel.filteredFares[0]?.fare;

    const currentTripViewModelMinFareViewModel = tripViewModels[index].filteredFares[0]?.fare;

    const cheapestTripViewModelFareWithFee = cheapestTripViewModelMinFareViewModel
      ? +cheapestTripViewModelMinFareViewModel.price.amountWithFraction.toFixed(
          numberOfDecimalsInCurrency,
        )
      : 0;
    const currentTripViewModelFareWithFee = currentTripViewModelMinFareViewModel
      ? +currentTripViewModelMinFareViewModel.price.amountWithFraction.toFixed(
          numberOfDecimalsInCurrency,
        )
      : 0;

    if (
      cheapestTripViewModelMinFareViewModel &&
      currentTripViewModelMinFareViewModel &&
      (currentTripViewModelFareWithFee < cheapestTripViewModelFareWithFee ||
        (currentTripViewModelFareWithFee === cheapestTripViewModelFareWithFee &&
          currentTripViewModelMinFareViewModel.score >
            cheapestTripViewModelMinFareViewModel.score) ||
        (currentTripViewModelFareWithFee === cheapestTripViewModelFareWithFee &&
          currentTripViewModelMinFareViewModel.score ===
            cheapestTripViewModelMinFareViewModel.score &&
          cheapestTripViewModel.legs[0].leg.departureTimeMinutes <
            tripViewModels[index].legs[0].leg.departureTimeMinutes))
    ) {
      cheapestTripViewModel = tripViewModels[index];
      cheapestTripViewModelIndex = index;
    }
  }

  return { cheapestTripViewModel, cheapestTripViewModelIndex };
}

/**
 * Highlights cheapest direct trip view models
 */
function highlightCheapestDirectTripViewModels(
  tripViewModels: Array<TripViewModel>,
  numberOfDecimalsInCurrency: number,
): Array<TripViewModel> {
  const { cheapestDirectTripViewModel, cheapestDirectTripViewModelIndex } =
    getCheapestDirectTripViewModel(tripViewModels, numberOfDecimalsInCurrency);

  if (cheapestDirectTripViewModel && cheapestDirectTripViewModelIndex !== undefined) {
    if (cheapestDirectTripViewModelIndex > 1) {
      const isFirstCardCheapest = tripViewModels[0].tripCardTags?.includes('cheapest');
      const indexOfCheapestDirect = isFirstCardCheapest ? 1 : 2;

      tripViewModels.splice(cheapestDirectTripViewModelIndex, 1);
      tripViewModels.splice(indexOfCheapestDirect, 0, cheapestDirectTripViewModel);
    }

    const cheapestDirectPrice =
      cheapestDirectTripViewModel.filteredFares[0]?.fare?.price.amountWithFraction || 0;

    const cheapestDirectPriceWithDecimals = +cheapestDirectPrice.toFixed(
      numberOfDecimalsInCurrency,
    );

    return tripViewModels.map((tripViewModel) => {
      const isTripViewModelDirect = tripViewModel.legs.every(
        (legViewModel) => legViewModel.leg.segments.length === 1,
      );

      if (isTripViewModelDirect) {
        const tripViewModelPrice =
          tripViewModel.filteredFares[0]?.fare?.price.amountWithFraction || 0;

        const tripViewModelPriceWithDecimals = +tripViewModelPrice.toFixed(
          numberOfDecimalsInCurrency,
        );

        if (tripViewModelPriceWithDecimals === cheapestDirectPriceWithDecimals) {
          return {
            ...tripViewModel,
            tripCardTags: [...(tripViewModel?.tripCardTags || []), 'cheapest_direct'],
          };
        }

        return tripViewModel;
      }

      return tripViewModel;
    });
  }

  return tripViewModels;
}

/**
 * Gets the cheapest direct trip view model
 */
function getCheapestDirectTripViewModel(
  tripViewModels: Array<TripViewModel>,
  numberOfDecimalsInCurrency: number,
): {
  cheapestDirectTripViewModel: TripViewModel | undefined;
  cheapestDirectTripViewModelIndex: number | undefined;
} {
  let cheapestDirectTripViewModel;
  let cheapestDirectTripViewModelIndex;

  for (let i = 0; i < tripViewModels.length; i++) {
    if (tripViewModels[i].legs.every((legViewModel) => legViewModel.leg.segments.length === 1)) {
      const currentTripViewModelFare = tripViewModels[i].filteredFares[0]?.fare;

      const currentTripViewModelPrice = currentTripViewModelFare
        ? +currentTripViewModelFare.price.amountWithFraction.toFixed(numberOfDecimalsInCurrency)
        : 0;

      const cheapestDirectTripViewModelFare = cheapestDirectTripViewModel?.filteredFares[0]?.fare;

      const cheapestDirectTripViewModelPrice = cheapestDirectTripViewModelFare
        ? +cheapestDirectTripViewModelFare.price.amountWithFraction.toFixed(
            numberOfDecimalsInCurrency,
          )
        : undefined;

      if (
        currentTripViewModelFare &&
        (!cheapestDirectTripViewModel ||
          !cheapestDirectTripViewModelPrice ||
          !cheapestDirectTripViewModelFare ||
          currentTripViewModelPrice < cheapestDirectTripViewModelPrice ||
          (currentTripViewModelPrice === cheapestDirectTripViewModelPrice &&
            currentTripViewModelFare.score > cheapestDirectTripViewModelFare.score) ||
          (currentTripViewModelPrice === cheapestDirectTripViewModelPrice &&
            currentTripViewModelFare.score === cheapestDirectTripViewModelFare.score &&
            tripViewModels[i].legs[0].leg.departureTimeMinutes <
              cheapestDirectTripViewModel.legs[0].leg.departureTimeMinutes))
      ) {
        cheapestDirectTripViewModel = tripViewModels[i];
        cheapestDirectTripViewModelIndex = i;
      }
    }
  }

  return { cheapestDirectTripViewModel, cheapestDirectTripViewModelIndex };
}

export function getFlightSortType(sortTypeSearchParam: string): FlightSortType {
  switch (sortTypeSearchParam.toLowerCase()) {
    case 'price':
    case 'duration':
    case 'score':
    case 'leg1_departure_time':
    case 'leg2_departure_time':
    case 'leg3_departure_time':
    case 'leg4_departure_time':
    case 'leg5_departure_time':
    case 'leg6_departure_time':
      return sortTypeSearchParam.toLowerCase() as FlightSortType;
    default:
      return 'price';
  }
}
